import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { skillLevelsApi } from '../services/api';
import { SkillLevel, CreateSkillLevelData } from '../types';

const SkillLevelsManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingSkillLevel, setEditingSkillLevel] = useState<SkillLevel | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: skillLevelsData, isLoading } = useQuery({
    queryKey: ['skill-levels', { page, search }],
    queryFn: () => skillLevelsApi.getAll({ page, limit: 10, search: search || undefined }),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateSkillLevelData>();

  const createMutation = useMutation({
    mutationFn: skillLevelsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skill-levels'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateSkillLevelData }) =>
      skillLevelsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skill-levels'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setEditingSkillLevel(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: skillLevelsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skill-levels'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  const onSubmit = (data: CreateSkillLevelData) => {
    if (editingSkillLevel) {
      updateMutation.mutate({ id: editingSkillLevel.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (skillLevel: SkillLevel) => {
    setEditingSkillLevel(skillLevel);
    reset({
      name: skillLevel.name,
      description: skillLevel.description || '',
      level: skillLevel.level,
    });
    setShowForm(true);
  };

  const handleDelete = (skillLevel: SkillLevel) => {
    if (window.confirm(`Are you sure you want to delete "${skillLevel.name}"?`)) {
      deleteMutation.mutate(skillLevel.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSkillLevel(null);
    reset();
  };

  return (
    <div className="skill-levels-management">
      <div className="page-header">
        <h1>Skill Levels Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          Add New Skill Level
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingSkillLevel ? 'Edit Skill Level' : 'Create New Skill Level'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  {...register('name', { required: 'Name is required' })}
                  type="text"
                  id="name"
                  className={errors.name ? 'error' : ''}
                  placeholder="e.g., Beginner, Intermediate, Advanced"
                />
                {errors.name && <span className="error-text">{errors.name.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="level">Level (1-10) *</label>
                <input
                  {...register('level', {
                    required: 'Level is required',
                    min: { value: 1, message: 'Level must be between 1 and 10' },
                    max: { value: 10, message: 'Level must be between 1 and 10' }
                  })}
                  type="number"
                  id="level"
                  min="1"
                  max="10"
                  className={errors.level ? 'error' : ''}
                />
                {errors.level && <span className="error-text">{errors.level.message}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                {...register('description')}
                id="description"
                rows={3}
                placeholder="Describe what defines this skill level..."
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingSkillLevel ? 'Update' : 'Create'} Skill Level
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search skill levels..."
          aria-label="Search skill levels"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {isLoading ? (
        <div className="loading">Loading skill levels...</div>
      ) : skillLevelsData && skillLevelsData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Level</th>
                  <th>Description</th>
                  <th>Moves Count</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {skillLevelsData.data.map((skillLevel) => (
                  <tr key={skillLevel.id}>
                    <td>
                      <strong>{skillLevel.name}</strong>
                    </td>
                    <td>
                      <span className="level-badge">Level {skillLevel.level}</span>
                    </td>
                    <td>{skillLevel.description || '-'}</td>
                    <td>{skillLevel.movesCount || 0}</td>
                    <td>{new Date(skillLevel.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleEdit(skillLevel)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(skillLevel)}
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {skillLevelsData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: skillLevelsData.pagination.pages }, (_, i) => i + 1).map(
                (pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn btn-small ${
                      pageNum === page ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              )}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No skill levels found. Create your first skill level!</p>
        </div>
      )}
    </div>
  );
};

export default SkillLevelsManagement;